import { __decorate, __metadata, __param } from "tslib";
import { inject, injectable } from "inversify";
import { BehaviorSubject } from "rxjs";
import { getKey } from "@smartsheet/cufflink";
import { LoggingStateService, loggingStateServiceKey } from "@smartsheet/logging-state-service";
import { Griddle } from "./griddle/Griddle";
import { ResponseUtil } from "./util/ResponseUtil";
import { gridStateServiceDIKey } from "./constants";
import { legacyGridBridgeDataClientDIKey } from "./legacyBridge";
import { GridServiceApiVersion } from "./griddle";
/**
 * State Service that provides data related to the grid. See [[GridState]].
 */
var GridStateServiceImpl = /** @class */ (function () {
    function GridStateServiceImpl(loggingStateService, legacyGridBridgeDataClient) {
        var _this = this;
        var _a, _b;
        this.updateLegacyGridState = function (value) {
            _this.gridState$.next(value);
        };
        /**
         * Gets baseline summary for the project
         * @returns Response<ProjectBaselineSummary> baseline summary object and result code
         */
        this.getProjectBaselineSummary = function () {
            try {
                return _this.legacyGridApi.getProjectBaselineSummary();
            }
            catch (error) {
                _this.loggingStateService.error("[getProjectBaselineSummary] Uncaught exception during request", error);
                return {
                    resultCode: "UNEXPECTED_ERROR"
                };
            }
        };
        /**
         * Get a GridBroker.
         *
         * Required for all grid data access, manipulations, and subscription based updates.
         *
         * !WARNING!
         * Not to be used in conjunction with any other grid-state-service method
         * that gets/accesses/modifies grid data or otherwise interacts with grid
         *
         * Typedoc recognizes this as a property, but it is definitely a method
         * @returns GridBroker
         */
        this.getBroker = new Griddle({
            clientSessionKey: (_b = (_a = window) === null || _a === void 0 ? void 0 : _a.clientState) === null || _b === void 0 ? void 0 : _b.sessionKey,
            gridServiceHosts: new Map([
                [GridServiceApiVersion.V2, "/2.0"],
                [GridServiceApiVersion.V2_ALPHA, "/2.0-alpha"]
            ]) // todo: don't hardcode this
        }).getBroker;
        /**
         * Creates new project baseline
         * @returns {ResultCode} result code
         */
        this.createProjectBaseline = function (displayBaselineBars) {
            try {
                return _this.legacyGridApi.createProjectBaseline(displayBaselineBars);
            }
            catch (error) {
                _this.loggingStateService.error("[createProjectBaseline] Uncaught exception during request", error);
                return "UNEXPECTED_ERROR";
            }
        };
        /**
         * Removes existing project baseline
         * @returns {ResultCode} result code
         */
        this.removeProjectBaseline = function () {
            try {
                return _this.legacyGridApi.removeProjectBaseline();
            }
            catch (error) {
                _this.loggingStateService.error("[removeProjectBaseline] Uncaught exception during request", error);
                return "UNEXPECTED_ERROR";
            }
        };
        /**
         * Resets existing project baseline
         * @returns {ResultCode} result code
         */
        this.resetProjectBaseline = function () {
            try {
                return _this.legacyGridApi.resetProjectBaseline();
            }
            catch (error) {
                _this.loggingStateService.error("[resetProjectBaseline] Uncaught exception during request", error);
                return "UNEXPECTED_ERROR";
            }
        };
        /**
         * Sets the baseline bars display in gantt view
         * @param boolean displayBaselineBars will set the display status of the baseline bars in gantt view
         * @returns {ResultCode} Result code
         */
        this.setBaselineBarsDisplay = function (displayBaselineBars) {
            try {
                return _this.legacyGridApi.setBaselineBarsDisplay(displayBaselineBars);
            }
            catch (error) {
                _this.loggingStateService.error("[setBaselineBarsDisplay] Uncaught exception during request", error);
                return "UNEXPECTED_ERROR";
            }
        };
        /**
         * Gets the current user's access level on the current grid
         * @returns Response<AccessLevel> current user's access level on the current grid and result code
         * @deprecated
         */
        this.getAccessLevel = function () {
            try {
                return _this.legacyGridApi.getAccessLevel();
            }
            catch (error) {
                _this.loggingStateService.error("[getAccessLevel] Uncaught exception during request", error);
                return {
                    resultCode: "UNEXPECTED_ERROR"
                };
            }
        };
        /**
         * Sets the legacy grid's automation column IDs - i.e., columns which have automated workflows associated with them.
         * By knowing about columns associated with workflows, the grid can warn a user about impacting an automation when
         * the user attempts to delete a column.
         * @param automationColumnIds current sheet's columns that have automated workflows associated with them
         * @deprecated
         */
        this.setAutomationColumnIds = function (automationColumnIds) {
            try {
                return _this.legacyGridApi.setAutomationColumnIds(automationColumnIds);
            }
            catch (error) {
                _this.loggingStateService.error("[setAutomationColumnIds] Uncaught exception during request", error);
                return {
                    resultCode: "UNEXPECTED_ERROR"
                };
            }
        };
        /**
         * Gets the total column count of the current grid
         * @returns Response<number> column count of the current grid and result code
         * @deprecated
         */
        this.getColumnCount = function () {
            try {
                return _this.legacyGridApi.getColumnCount();
            }
            catch (error) {
                _this.loggingStateService.error("[getColumnCount] Uncaught exception during request", error);
                return {
                    resultCode: "UNEXPECTED_ERROR"
                };
            }
        };
        /**
         * Gets the total row count of the current grid
         * @returns Response<number> row count of the current grid and result code
         * @deprecated
         */
        this.getRowCount = function () {
            try {
                return _this.legacyGridApi.getRowCount();
            }
            catch (error) {
                _this.loggingStateService.error("[getRowCount] Uncaught exception during request", error);
                return {
                    resultCode: "UNEXPECTED_ERROR"
                };
            }
        };
        /**
         * @param rowId
         * @param columnId
         * @deprecated
         */
        this.getCellByRowAndColumnId = function (rowId, columnId) {
            try {
                return _this.legacyGridApi.getCellByRowAndColumnId(rowId, columnId);
            }
            catch (error) {
                _this.loggingStateService.error("[getCellByRowAndColumnId] Uncaught exception during request", error);
                return {
                    resultCode: "UNEXPECTED_ERROR"
                };
            }
        };
        /**
         * Gets all the legacy columns of the current grid
         * @returns Response<LegacyColumn[]> legacy columns of the current grid and result code
         * @deprecated
         */
        this.getColumns = function () {
            try {
                return _this.legacyGridApi.getColumns();
            }
            catch (error) {
                _this.loggingStateService.error("[getColumns] Uncaught exception during request", error);
                return {
                    resultCode: "UNEXPECTED_ERROR"
                };
            }
        };
        /**
         * Gets the legacy columns of given indexes from the current grid, in the same order as their indexes
         * @param number[] ordered list of indexes for grid columns.
         * @returns Response<LegacyColumn[]> legacy columns of the current grid and result code
         * @deprecated
         */
        this.getColumnsByIndex = function (indexes) {
            try {
                return _this.legacyGridApi.getColumnsByIndex(indexes);
            }
            catch (error) {
                _this.loggingStateService.error("[getColumnsByIndex] Uncaught exception during request", error);
                return {
                    resultCode: "UNEXPECTED_ERROR"
                };
            }
        };
        /**
         * Gets the legacy rows of given indexes from the current grid, in the same order as their indexes
         * @param number[] ordered list of indexes for grid rows.
         * @returns Response<LegacyRow[]> legacy rows of the current grid and result code
         * @deprecated
         */
        this.getRowsByIndex = function (indexes) {
            try {
                return _this.legacyGridApi.getRowsByIndex(indexes);
            }
            catch (error) {
                _this.loggingStateService.error("[getRowsByIndex] Uncaught exception during request", error);
                return {
                    resultCode: "UNEXPECTED_ERROR"
                };
            }
        };
        this.insertColumnAndSave = function (columnName, columnType, validationType, insertCallback, pickListData, isLocked, columnDescription, targetIndex) {
            try {
                return _this.legacyGridApi.insertColumnAndSave(columnName, columnType, validationType, insertCallback, pickListData, isLocked, columnDescription, targetIndex);
            }
            catch (error) {
                _this.loggingStateService.error("[insertColumnAndSave] Uncaught exception during request", error);
                return {
                    resultCode: "UNEXPECTED_ERROR"
                };
            }
        };
        this.removeColumnAndSave = function (columnId) {
            try {
                return _this.legacyGridApi.removeColumnAndSave(columnId);
            }
            catch (error) {
                _this.loggingStateService.error("[removeColumnAndSave] Uncaught exception during request", error);
                return {
                    resultCode: "UNEXPECTED_ERROR"
                };
            }
        };
        /**
         * Subscribe to this state service. The returned object implements the {@link https://rxjs-dev.firebaseapp.com/api/index/interface/SubscriptionLike | SubscriptionLike from the RxJS library}.
         *
         * Don't forget to call unsubscribe on the returned object when destroying the component that's using it.
         *
         * @param handler the action you want to take when the subscription is updated
         */
        this.subscribe = function (handler) { return _this.gridState$.subscribe(handler); };
        /**
         * Returns the current {@link GridState}. This is useful if you need to access grid state outside of the direct context
         * of the subscription handler passed to the subscribe method.
         */
        this.getCurrentState = function () { return _this.gridState$.getValue(); };
        this.loggingStateService = loggingStateService;
        this.legacyGridApi = legacyGridBridgeDataClient.legacyGridApi;
        this.gridState$ = new BehaviorSubject({
            activeGridId: null
        });
    }
    GridStateServiceImpl.prototype.isLoadComplete = function () {
        try {
            return ResponseUtil.success(this.legacyGridApi.isLoadComplete());
        }
        catch (error) {
            this.loggingStateService.error("[isLoadComplete] Uncaught exception during request", error);
            return ResponseUtil.error();
        }
    };
    GridStateServiceImpl.prototype.isRowFilteredOut = function (rowId) {
        try {
            return ResponseUtil.success(this.legacyGridApi.isRowFilteredOut(rowId));
        }
        catch (error) {
            this.loggingStateService.error("[isRowFilteredOut] Uncaught exception during request", error);
            return ResponseUtil.error();
        }
    };
    GridStateServiceImpl.prototype.isRowLockRestricted = function (rowId) {
        try {
            return ResponseUtil.success(this.legacyGridApi.isRowLockRestricted(rowId));
        }
        catch (error) {
            this.loggingStateService.error("[isRowLockRestricted] Uncaught exception during request", error);
            return ResponseUtil.error();
        }
    };
    GridStateServiceImpl.prototype.isRowSelectable = function (rowId) {
        try {
            return ResponseUtil.success(this.legacyGridApi.isRowSelectable(rowId));
        }
        catch (error) {
            this.loggingStateService.error("[isRowSelectable] Uncaught exception during request", error);
            return ResponseUtil.error();
        }
    };
    GridStateServiceImpl.prototype.refreshDiscussionIconForRow = function (rowId, balloonType, updatedValue) {
        try {
            return ResponseUtil.success(this.legacyGridApi.refreshDiscussionIconForRow(rowId, balloonType, updatedValue));
        }
        catch (error) {
            this.loggingStateService.error("[refreshDiscussionIconForRow] Uncaught exception during request", error);
            return ResponseUtil.error();
        }
    };
    GridStateServiceImpl.prototype.rowExists = function (rowId) {
        try {
            return ResponseUtil.success(this.legacyGridApi.rowExists(rowId));
        }
        catch (error) {
            this.loggingStateService.error("[rowExists] Uncaught exception during request", error);
            return ResponseUtil.error();
        }
    };
    GridStateServiceImpl.prototype.rowExistsForGrid = function (rowId, gridId) {
        try {
            return ResponseUtil.success(this.legacyGridApi.rowExistsForGrid(rowId, gridId));
        }
        catch (error) {
            this.loggingStateService.error("[rowExistsForGrid] Uncaught exception during request", error);
            return ResponseUtil.error();
        }
    };
    GridStateServiceImpl.prototype.saveRowForCallback = function (rowId, callback, forceSave, rightPanelRequest) {
        try {
            return ResponseUtil.success(this.legacyGridApi.saveRowForCallback(rowId, callback, forceSave, rightPanelRequest));
        }
        catch (error) {
            this.loggingStateService.error("[saveRowForCallback] Uncaught exception during request", error);
            return ResponseUtil.error();
        }
    };
    GridStateServiceImpl.prototype.updateGridDataTimestampByGridId = function (gridId, dataTimestamp, modifyDateTime) {
        try {
            return ResponseUtil.success(this.legacyGridApi.updateGridDataTimestampByGridId(gridId, dataTimestamp, modifyDateTime));
        }
        catch (error) {
            this.loggingStateService.error("[updateGridDataTimestampByGridId] Uncaught exception during request", error);
            return ResponseUtil.error();
        }
    };
    GridStateServiceImpl.prototype.useTemplate = function (gridId) {
        try {
            this.legacyGridApi.useTemplate(gridId);
        }
        catch (error) {
            this.loggingStateService.error("[useTemplate] Uncaught exception during request", error);
        }
    };
    GridStateServiceImpl = __decorate([
        injectable(),
        __param(0, inject(loggingStateServiceKey.name)),
        __param(1, inject(legacyGridBridgeDataClientDIKey)),
        __metadata("design:paramtypes", [LoggingStateService, Object])
    ], GridStateServiceImpl);
    return GridStateServiceImpl;
}());
export { GridStateServiceImpl };
/**
 * Pass this key to Cufflink to be used in retrieval of an instance of this state service.
 */
export var gridStateServiceKey = getKey(gridStateServiceDIKey, GridStateServiceImpl);
//# sourceMappingURL=GridStateService.js.map